import React, { useState, useMemo } from "react";
import { Card, Space, Spin, Alert, Collapse, Input, Typography, Tag, Statistic, Empty, Tabs, Button, message, type CollapseProps } from "antd";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import { useAbrangencia } from "../../hooks/useAbrangencia";
import { useTheme } from "../../hooks/useTheme";
import abrangencia from "../../services/abrangencia";
import type { Platform, Search, Source } from "../../services/abrangencia";
import { exportAbrangenciaToXlsx } from "../../utils/exportAbrangencia";

const { Text } = Typography;
/**
 * Componente de conteúdo da abrangência (reutilizável para pública e segredo)
 */
const AbrangenciaContent: React.FC<{
  data: any;
  loading: boolean;
  error: string | null;
}> = ({ data: propData, loading: propLoading, error: propError }) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { theme } = useTheme();
  
  const data = propData;
  const loading = propLoading;
  const error = propError;
  
  // Cores adaptáveis ao tema
  const isDark = theme === "dark";
  const summaryCardBg = isDark ? "#1f1f1f" : "#f5f5f5";
  const summaryCardText = isDark ? "#ffffff" : "#000000d9";
  const summaryCardLabel = isDark ? "#ffffffa6" : "#00000073";
  const searchCardBg = isDark ? "#111b26" : "#e6f7ff";
  const searchCardBorder = isDark ? "#1890ff" : "#91d5ff";
  const searchCardText = isDark ? "#69b7ff" : "#1890ff";
  const paramCardBg = isDark ? "#1f1f1f" : "#fafafa";

  // Filtrar dados baseado no termo de busca
  const filteredPlatforms = useMemo(() => {
    if (!data || data.length === 0) return [];

    const allPlatforms: Platform[] = [];
    data.forEach((source: any) => {
      source.platforms.forEach((platform: Platform) => {
        allPlatforms.push(platform);
      });
    });

    if (!searchTerm) return allPlatforms;

    const normalizeText = (text: string): string => {
      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    };

    const matchesSearch = (obj: any, term: string): boolean => {
      if (!term) return true;
      const normalizedTerm = normalizeText(term);
      const searchInString = (str: string): boolean => {
        return normalizeText(str).includes(normalizedTerm);
      };
      const searchInObject = (obj: any): boolean => {
        if (typeof obj === "string") {
          return searchInString(obj);
        }
        if (Array.isArray(obj)) {
          return obj.some((item) => searchInObject(item));
        }
        if (obj && typeof obj === "object") {
          return Object.values(obj).some((value) => searchInObject(value));
        }
        return false;
      };
      return searchInObject(obj);
    };

    return allPlatforms
      .map((platform) => {
        const platformMatches = matchesSearch(platform, searchTerm);
        if (platformMatches) {
          return platform;
        }
        const filteredSearches = platform.searches
          .map((search) => {
            const searchMatches = matchesSearch(search, searchTerm);
            if (searchMatches) {
              return search;
            }
            const filteredQueries = search.queries.filter((query) => matchesSearch(query, searchTerm));
            if (filteredQueries.length > 0) {
              return {
                ...search,
                queries: filteredQueries,
              };
            }
            return null;
          })
          .filter((search): search is Search => search !== null);
        if (filteredSearches.length > 0) {
          return {
            ...platform,
            searches: filteredSearches,
          };
        }
        return null;
      })
      .filter((platform): platform is Platform => platform !== null);
  }, [data, searchTerm]);

  const allPlatforms = useMemo(() => {
    if (!data || data.length === 0) return [];
    const platforms: Platform[] = [];
    data.forEach((source: any) => {
      source.platforms.forEach((platform: Platform) => {
        platforms.push(platform);
      });
    });
    return platforms;
  }, [data]);

  const totalStatistics = useMemo(() => {
    if (!allPlatforms || allPlatforms.length === 0) {
      return { platforms: 0, searches: 0, queries: 0, params: 0 };
    }
    let searches = 0;
    let queries = 0;
    let params = 0;
    allPlatforms.forEach((platform) => {
      searches += platform.searches.length;
      platform.searches.forEach((search) => {
        queries += search.queries.length;
        search.queries.forEach((query) => {
          params += query.params.length;
        });
      });
    });
    return {
      platforms: allPlatforms.length,
      searches,
      queries,
      params,
    };
  }, [allPlatforms]);

  const filteredStatistics = useMemo(() => {
    if (!filteredPlatforms || filteredPlatforms.length === 0) {
      return { platforms: 0, searches: 0, queries: 0, params: 0 };
    }
    let searches = 0;
    let queries = 0;
    let params = 0;
    filteredPlatforms.forEach((platform) => {
      searches += platform.searches.length;
      platform.searches.forEach((search) => {
        queries += search.queries.length;
        search.queries.forEach((query) => {
          params += query.params.length;
        });
      });
    });
    return {
      platforms: filteredPlatforms.length,
      searches,
      queries,
      params,
    };
  }, [filteredPlatforms]);

  const collapseItems: CollapseProps["items"] = useMemo(
    () =>
      filteredPlatforms.map((platform, platformIdx) => ({
        key: `platform-${platformIdx}`,
        label: (
          <Space>
            <Tag color="green">{platform.platform}</Tag>
            <Text>{platform.description}</Text>
            <Tag color="default">{platform.searches.length} busca(s)</Tag>
          </Space>
        ),
        children: (
          <Collapse
            items={platform.searches.map((search, searchIdx) => ({
              key: `search-${platformIdx}-${searchIdx}`,
              label: (
                <Space>
                  <Tag color="orange">{search.search}</Tag>
                  <Text>{search.description}</Text>
                  <Tag color="default">{search.queries.length} consulta(s)</Tag>
                </Space>
              ),
              children: (
                <Collapse
                  items={search.queries.map((query, queryIdx) => ({
                    key: `query-${platformIdx}-${searchIdx}-${queryIdx}`,
                    label: (
                      <Space>
                        <Tag color="purple">{query.query}</Tag>
                        <Text>{query.description}</Text>
                        <Tag color="default">{query.params.length} parâmetro(s)</Tag>
                      </Space>
                    ),
                    children: (
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        {query.params.map((param, paramIdx) => (
                          <Card
                            key={paramIdx}
                            size="small"
                            style={{
                              backgroundColor: paramCardBg,
                              border: isDark ? "1px solid #434343" : undefined,
                            }}
                          >
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                              <div>
                                <Text strong style={{ color: isDark ? "#ffffff" : undefined }}>
                                  {param.label}
                                </Text>
                                {param.required && (
                                  <Tag color="red" style={{ marginLeft: 8 }}>
                                    Obrigatório
                                  </Tag>
                                )}
                                {!param.required && (
                                  <Tag color="default" style={{ marginLeft: 8 }}>
                                    Opcional
                                  </Tag>
                                )}
                              </div>
                              <div>
                                <Text
                                  style={{
                                    marginRight: 8,
                                    color: isDark ? "#ffffff73" : undefined,
                                  }}
                                >
                                  Tag:
                                </Text>
                                <Text code>{param.tag}</Text>
                              </div>
                              {param.mask && (
                                <div>
                                  <Text
                                    style={{
                                      marginRight: 8,
                                      color: isDark ? "#ffffff73" : undefined,
                                    }}
                                  >
                                    Máscara:
                                  </Text>
                                  <Text code>{param.mask}</Text>
                                </div>
                              )}
                            </Space>
                          </Card>
                        ))}
                      </Space>
                    ),
                  }))}
                />
              ),
            }))}
          />
        ),
      })),
    [filteredPlatforms, isDark, paramCardBg]
  );

  if (loading) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "40px" }} />
        </Space>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Alert message="Erro ao carregar abrangências" description={error} type="error" showIcon />
        </Space>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Empty description="Nenhuma abrangência disponível." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Space>
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Resumo Geral */}
      <Card 
        size="small" 
        style={{ 
          backgroundColor: summaryCardBg,
          border: isDark ? "1px solid #434343" : undefined,
        }}
      >
        <div style={{ marginBottom: searchTerm ? "16px" : "0" }}>
          <Text 
            strong 
            style={{ 
              fontSize: "12px", 
              textTransform: "uppercase",
              color: summaryCardLabel,
            }}
          >
            Total Geral
          </Text>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "space-around" }}>
          <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
            <div style={{ color: summaryCardLabel, marginBottom: 4, fontSize: 14 }}>
              Plataformas
            </div>
            <Statistic 
              value={totalStatistics.platforms}
              valueStyle={{ color: summaryCardText }}
            />
          </div>
          <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
            <div style={{ color: summaryCardLabel, marginBottom: 4, fontSize: 14 }}>
              Buscas
            </div>
            <Statistic 
              value={totalStatistics.searches}
              valueStyle={{ color: summaryCardText }}
            />
          </div>
          <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
            <div style={{ color: summaryCardLabel, marginBottom: 4, fontSize: 14 }}>
              Consultas
            </div>
            <Statistic 
              value={totalStatistics.queries}
              valueStyle={{ color: summaryCardText }}
            />
          </div>
          <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
            <div style={{ color: summaryCardLabel, marginBottom: 4, fontSize: 14 }}>
              Parâmetros
            </div>
            <Statistic 
              value={totalStatistics.params}
              valueStyle={{ color: summaryCardText }}
            />
          </div>
        </div>
      </Card>

      {/* Filtro de Busca */}
      <Input size="large" placeholder="Buscar por qualquer chave (plataforma, busca, consulta, parâmetro, descrição...)" prefix={<SearchOutlined />} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} allowClear />

      {/* Resumo da Busca */}
      {searchTerm && (
        <Card 
          size="small" 
          style={{ 
            backgroundColor: searchCardBg, 
            border: `1px solid ${searchCardBorder}`,
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <Text 
              strong 
              style={{ 
                fontSize: "12px", 
                textTransform: "uppercase", 
                color: searchCardText,
              }}
            >
              Resultado da Busca: "{searchTerm}"
            </Text>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "space-around" }}>
            <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
              <div style={{ color: isDark ? "#ffffff73" : "#00000073", marginBottom: 4, fontSize: 14 }}>
                Plataformas
              </div>
              <Statistic 
                value={filteredStatistics.platforms} 
                suffix={`/ ${totalStatistics.platforms}`} 
                valueStyle={{ 
                  color: filteredStatistics.platforms < totalStatistics.platforms 
                    ? (isDark ? "#69b7ff" : "#1890ff") 
                    : (isDark ? "#73d13d" : "#3f8600"),
                }}
              />
            </div>
            <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
              <div style={{ color: isDark ? "#ffffff73" : "#00000073", marginBottom: 4, fontSize: 14 }}>
                Buscas
              </div>
              <Statistic 
                value={filteredStatistics.searches} 
                suffix={`/ ${totalStatistics.searches}`} 
                valueStyle={{ 
                  color: filteredStatistics.searches < totalStatistics.searches 
                    ? (isDark ? "#69b7ff" : "#1890ff") 
                    : (isDark ? "#73d13d" : "#3f8600"),
                }}
              />
            </div>
            <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
              <div style={{ color: isDark ? "#ffffff73" : "#00000073", marginBottom: 4, fontSize: 14 }}>
                Consultas
              </div>
              <Statistic 
                value={filteredStatistics.queries} 
                suffix={`/ ${totalStatistics.queries}`} 
                valueStyle={{ 
                  color: filteredStatistics.queries < totalStatistics.queries 
                    ? (isDark ? "#69b7ff" : "#1890ff") 
                    : (isDark ? "#73d13d" : "#3f8600"),
                }}
              />
            </div>
            <div style={{ flex: "1", minWidth: "120px", textAlign: "center" }}>
              <div style={{ color: isDark ? "#ffffff73" : "#00000073", marginBottom: 4, fontSize: 14 }}>
                Parâmetros
              </div>
              <Statistic 
                value={filteredStatistics.params} 
                suffix={`/ ${totalStatistics.params}`} 
                valueStyle={{ 
                  color: filteredStatistics.params < totalStatistics.params 
                    ? (isDark ? "#69b7ff" : "#1890ff") 
                    : (isDark ? "#73d13d" : "#3f8600"),
                }}
              />
            </div>
          </div>
          <div style={{ marginTop: "12px", textAlign: "center" }}>
            <Text 
              style={{ 
                fontSize: "12px",
                color: isDark ? "#ffffff73" : "#00000073",
              }}
            >
              Mostrando {filteredStatistics.platforms} de {totalStatistics.platforms} plataforma(s) correspondente(s) à busca
            </Text>
          </div>
        </Card>
      )}

      {/* Lista de Abrangências */}
      {filteredPlatforms.length === 0 ? (
        <Empty description="Nenhum resultado encontrado para a busca." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Collapse items={collapseItems} />
      )}
    </Space>
  );
};

/**
 * Componente principal da página Abrangencia.
 */
const Abrangencia: React.FC = () => {
  const { data: publicData, loading: publicLoading, error: publicError } = useAbrangencia();
  const [secretData, setSecretData] = useState<Source[] | null>(null);
  const [secretLoading, setSecretLoading] = useState<boolean>(false);
  const [secretError, setSecretError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("public");
  const [downloading, setDownloading] = useState<boolean>(false);

  // Buscar dados de segredo de justiça quando a aba for selecionada
  const fetchSecretData = async () => {
    if (secretData !== null) return; // Já foi carregado
    
    setSecretLoading(true);
    setSecretError(null);
    try {
      const response = await abrangencia.getAvailableJusticeSecret();
      if (response.success) {
        setSecretData(response.data);
      } else {
        setSecretError('Erro ao buscar abrangências de segredo de justiça');
      }
    } catch (err: any) {
      setSecretError(err.response?.data?.message || 'Erro ao buscar abrangências de segredo de justiça');
      console.error('Erro ao buscar abrangências de segredo de justiça:', err);
    } finally {
      setSecretLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === "secret") {
      fetchSecretData();
    }
  };

  // Exporta ambas as abrangências em um único arquivo XLSX com duas abas
  const handleDownload = async () => {
    if (!publicData || publicData.length === 0) {
      message.warning('Aguarde o carregamento dos dados públicos antes de exportar.');
      return;
    }

    setDownloading(true);
    try {
      // Busca segredo de justiça sob demanda caso ainda não tenha sido carregado
      let secret = secretData;
      if (secret === null) {
        const response = await abrangencia.getAvailableJusticeSecret();
        if (response.success) {
          secret = response.data;
          setSecretData(secret);
        } else {
          message.error('Erro ao buscar dados de segredo de justiça para exportação.');
          return;
        }
      }

      exportAbrangenciaToXlsx(publicData, secret ?? []);
      message.success('Arquivo exportado com sucesso!');
    } catch (err: any) {
      message.error('Erro ao gerar o arquivo de exportação.');
      console.error('Erro ao exportar abrangência:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Button
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownload}
              disabled={publicLoading}
            >
              Exportar XLSX
            </Button>
          }
          items={[
            {
              key: "public",
              label: "Pública",
              children: (
                <AbrangenciaContent 
                  data={publicData || []}
                  loading={publicLoading}
                  error={publicError}
                />
              ),
            },
            {
              key: "secret",
              label: "Segredo de Justiça",
              children: (
                <AbrangenciaContent 
                  data={secretData || []}
                  loading={secretLoading}
                  error={secretError}
                />
              ),
            },
          ]}
        />
      </Card>
  );
};

export default Abrangencia;
